import { type AdminModel, MODEL_ROLES, type ModelRole } from "@esi/shared";
import { ApiError } from "../api-errors.ts";
import { formatList } from "../i18n/format.ts";
import { t } from "../i18n/i18n.ts";

/**
 * Plain words for the model policy (model-policy-and-transparency): efforts, the roles a list
 * leaves uncovered, and each model's implications. Catalogue display names only, never IDs.
 */

/** The roles in a stable order, worded as what they serve ("titles, checks and summaries"). */
export function roleList(roles: readonly string[]): string {
  const known = MODEL_ROLES.filter((r) => roles.includes(r));
  return formatList(known.map((r) => t(`models.roles.${r}`)));
}

/** The roles of a refused `models.allowedModels` write, worded; null for another refusal. */
export function uncoveredRefusal(error: unknown): string | null {
  return error instanceof ApiError && error.code === "model_roles_uncovered" && error.roles
    ? t("models.blocked", { roles: roleList(error.roles) })
    : null;
}

/**
 * The needed roles a list leaves without a usable model: for each needed role, none of its
 * usable candidates (residency, availability, consent) is in `selected`.
 */
export function uncoveredBy(
  selected: readonly string[],
  roles: readonly { role: ModelRole; needed: boolean; candidates: readonly string[] }[],
): ModelRole[] {
  return roles
    .filter((r) => r.needed && !r.candidates.some((key) => selected.includes(key)))
    .map((r) => r.role);
}

/** One model's implications as short plain sentences, for the catalogue and the consent dialog. */
export function implicationLines(model: Pick<AdminModel, "implications">): string[] {
  const i = model.implications;
  return [
    i.retention === "aws_review"
      ? t("models.implications.retentionReview")
      : t("models.implications.retentionDefault"),
    i.geographies.includes("eu")
      ? t("models.implications.geographiesEu")
      : t("models.implications.geographiesGlobal"),
    ...(i.providerReceivesData ? [] : [t("models.implications.provider")]),
    t("models.implications.costSpeed", { cost: i.relativeCost, speed: i.relativeSpeed }),
  ];
}
